import { useState, useCallback, useMemo } from 'react';
import { useMediaQuery, useTheme, Box,
  CircularProgress, FormControl, InputLabel, MenuItem, Select,
} from '@mui/material';
import FlexCenter from 'components/FlexCenter';
import PieChart from 'components/chartNivo/PieChart';
import StackedBarChart from 'components/chartNivo/StackedBarChart';
import BarLineChart from 'components/chartNivo/BarLineChart';
import ChartDialog from 'components/chartNivo/ChartDialog';
import { boxStyles as styles } from 'components/CustomStyles';


const VIEW_OPTIONS = [
  { value: 'flightsByFlightReg', label: 'Flight by Reg' },
  { value: 'dailyFlightsByFlightReg', label: 'Daily Flight by Reg' },
  { value: 'flightsByACType', label: 'Flight by AC Type' },
  { value: 'flightsByFlightDate', label: 'Flight by Date' },
  { value: 'flightsByDep', label: 'Flight by Port' },
  { value: 'flightsBySector', label: 'Flight by Sector' },
  { value: 'flightsByFlightType', label: 'Flight by Type' },
];

export function ChartsCard({ data }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [chartOpen, setChartOpen] = useState(false);
  const [view, setView] = useState('flightsByFlightReg');

  const chartMap = useMemo(() => ({
    flightsByFlightReg:     { component: StackedBarChart, props: { bottomLegend: "Flight Reg", leftLegend: "Flights", bottomOffSet: -80, bottomLegendOffSet: 5 } },
    dailyFlightsByFlightReg:{ component: BarLineChart,    props: { bottomLegend: "Flight Reg", leftLegend: "Value",   bottomOffSet: -80, bottomLegendOffSet: 5 } },
    flightsByACType:        { component: PieChart,        props: { itemWidthOffSet: isMobile ? -50 : -40, translateXOffSet: isMobile ? -15 : -10 } },
    flightsByFlightDate:    { component: StackedBarChart, props: { bottomLegend: "Date",       leftLegend: "Flights", bottomOffSet: -90, bottomLegendOffSet: -5 } },
    flightsByDep:           { component: StackedBarChart, props: { bottomLegend: "Port",       leftLegend: "Flights", bottomOffSet: -95, bottomLegendOffSet: -10 } },
    flightsBySector:        { component: StackedBarChart, props: { bottomLegend: "Sector",     leftLegend: "Flights", bottomOffSet: -75, bottomLegendOffSet: 10 } },
    flightsByFlightType:    { component: PieChart,        props: { itemWidthOffSet: 15 } },
  }), [isMobile]);

  const renderChart = useCallback((v, styles) => {
    const chart = chartMap[v];
    if (!chart) return <Box pl={1} sx={styles}>No charts available</Box>;

    if (!data[v] || data[v].length === 0) {
      return <FlexCenter sx={styles}><CircularProgress size={30} /></FlexCenter>;
    }

    const ChartComponent = chart.component;
    return (
      <Box sx={{ ...styles }}>
        <ChartComponent data={data[v]} {...chart.props} />
      </Box>
    );
  }, [chartMap, data]);

  const handleOpen  = useCallback(() => setChartOpen(true),  []);
  const handleClose = useCallback(() => setChartOpen(false), []);


  return (
    <Box 
      id="charts"
      component="section"
      display="flex" 
      flexDirection="column"
      height={450} 
      sx={{
        borderRadius: 1,
        border: "1px solid",
        borderColor: "neutral.deep",
        backgroundColor: "background.default",
      }}
    > 
      <Box p={1}>
        <FormControl size="small">
          <InputLabel>View</InputLabel>
          <Select
            variant="outlined"
            label="View"
            value={view}
            onChange={(e) => setView(e.target.value)}
          >
            {VIEW_OPTIONS.map(({ value, label }) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box flex={1} overflow="hidden" onDoubleClick={handleOpen}>
        {renderChart(view, { height: "100%" })}
      </Box>

      {chartOpen && (
        <ChartDialog
          open={chartOpen}
          onClose={handleClose}
          chartComponent={renderChart(view, {...styles, pt: 3})}
        />
      )}
    </Box>
  );
}
